import React, { useCallback, useEffect, useMemo } from 'react';
import { Tabs, Button, Menu } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Statuses from '../../modules/Status/Statuses';
import { RoleList } from '../Role/RoleList';
import DiscountRate from '../DiscountRate/DiscountRate';
import ToolList from '../../components/Tools/ToolList';
import ParameterList from '../../components/Parameters/ParameterList';
import OfferingList from '../../components/Offerings/OfferingList';
import GroupList from '../../modules/groups/views/List';
import AgreementList from '../../components/Agreements/AgreementList';
import AnnouncementList from '../../components/Announcements/AnnouncementList';
import ProfessionList from '../../modules/professions/views/List';
import MeasuringInstrumentList from '../../modules/measuring-instruments/views/List';
import PublicHolidayList from '../../modules/public-holidays/views/List';
import MeasuringInstrumentInventoryList from '../../modules/measuring-instrument-inventories/views/List';
import MeasuringInstrumentCalibrationList from '../../modules/measuring-instrument-calibrations/views/List';
import MeasuringInstrumentInspectorList from '../../modules/measuring-instrument-inspectors/views/List';
import InspectorList from '../../modules/inspectors/views/List';
import InspectorProfessionList from '../../modules/inspector-professions/views/List';
import LeaveList from '../../modules/leaves/views/List';
import ExternalDocumentList from '../../modules/external-documents/views/List';
import ReportNodeList from '../../modules/report-nodes/views/List';
import QuestionList from '../../modules/questions/views/List';
import AnswerList from '../../modules/answers/views/List';
import InspectionAppointmentList from '../../modules/inspection-appointments/views/List';
import InspectionTaskList from '../../modules/inspection-tasks/views/List';
import ControlAreaList from '../../modules/control-areas/views/List';
import SettingKeyList from '../../components/Settings/SettingKeyList';
import NegativeProposalReasonList from '../../components/Settings/NegativeProposalReasonList';
import NonconformityReasonList from '../../components/Settings/NonconformityReasonList';
import OrganizationChartSettings from '../../components/Settings/OrganizationChartSettings';
import SmsTestList from '../../components/Settings/SmsTestList';
import StateList from '../../modules/states/views/List';
import DistrictList from '../../modules/districts/views/List';
import { hasRole } from '../../utils';
import NotFoundPage from '../NotFound';
import AccessInventory from '../../components/Settings/AccessInventory';

const { TabPane } = Tabs;

const Settings: React.FC = () => {
    const navigate = useNavigate();
    const { tab } = useParams<{ tab?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const isAdmin = hasRole('ADMIN');

    useEffect(() => {
        if (tab === 'organizastion') {
            navigate('/settings/organization', { replace: true });
        }
    }, [navigate, tab]);

    const parseNumericParam = (value: string | null): number | undefined => {
        if (value === null || value === '') {
            return undefined;
        }
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : undefined;
    };

    const questionToolIdValue = parseNumericParam(searchParams.get('questionToolId'));
    const questionAttributeIdValue = parseNumericParam(searchParams.get('questionAttributeId'));

    const updateSearchParam = useCallback(
        (key: string, value?: number) => {
            const next = new URLSearchParams(searchParams);
            if (value === undefined || value === null) {
                next.delete(key);
            } else {
                next.set(key, String(value));
            }
            setSearchParams(next);
        },
        [searchParams, setSearchParams],
    );

    const handleToolQuestionDrawerChange = useCallback(
        (visible: boolean, toolId?: number) => {
            if (visible && toolId !== undefined && toolId !== null) {
                updateSearchParam('questionToolId', toolId);
            } else {
                updateSearchParam('questionToolId');
            }
        },
        [updateSearchParam],
    );

    const handleParameterQuestionDrawerChange = useCallback(
        (visible: boolean, attributeId?: number) => {
            if (visible && attributeId !== undefined && attributeId !== null) {
                updateSearchParam('questionAttributeId', attributeId);
            } else {
                updateSearchParam('questionAttributeId');
            }
        },
        [updateSearchParam],
    );

    // General sekmesi altındaki alt sayfalar
    const adminOnlyGeneralSlugs = useMemo(() => ['states', 'districts', 'access-inventory'], []);
    const generalSlugs = useMemo(
        () => ['roles', 'sms-tests', 'agreements', 'announcements', 'public-holidays', 'organization', ...(isAdmin ? adminOnlyGeneralSlugs : []), 'system-settings'],
        [adminOnlyGeneralSlugs, isAdmin],
    );

    // Teklif Yönetimi sekmesi altındaki sayfalar
    const proposalSlugs = useMemo(() => ['statuses', 'discount-rates', 'tools', 'parameters', 'control-areas', 'offerings', 'groups', 'negative-reasons', 'nonconformity-reasons'], []);
    // Modüller sekmesi altındaki sayfalar (gruplara ayrılacak)
    const modulesGroups = useMemo(() => ([
        { key: 'grp-professions', label: 'Meslekler', slugs: ['professions'] },
        { key: 'grp-instruments', label: 'Ölçüm Cihazları', slugs: ['measuring-instrument-inventories','measuring-instruments'] },
        { key: 'grp-inspectors', label: 'P.K.M Uzmanları', slugs: ['inspectors'] },
        { key: 'grp-external-docs', label: 'Dış Kaynaklı Dokümanlar', slugs: ['external-documents'] },
        // Rapor Formatı sekmesi kaldırıldı
        // { key: 'grp-inspections', label: 'Denetimler', slugs: ['inspection-appointments','inspection-tasks'] },
    ]), []);
    const modulesSlugs = useMemo(() => modulesGroups.flatMap(g => g.slugs), [modulesGroups]);

    const activeKey = useMemo<string>(() => {
        if (!tab) return 'general';
        if (generalSlugs.includes(tab)) return 'general';
        if (proposalSlugs.includes(tab)) return 'proposals';
        if (modulesSlugs.includes(tab)) {
            const grp = modulesGroups.find(g => g.slugs.includes(tab));
            return grp ? grp.key : 'general';
        }
        return 'general';
    }, [tab, generalSlugs, proposalSlugs, modulesSlugs, modulesGroups]);

    if (tab === 'countries') {
        return <NotFoundPage />;
    }

    if (tab && adminOnlyGeneralSlugs.includes(tab) && !isAdmin) {
        return <NotFoundPage />;
    }

    const generalMenuItems = [
        { key: 'roles', label: 'Roller' },
        { key: 'sms-tests', label: 'SMS Test' },
        { key: 'agreements', label: 'Sözleşmeler' },
        { key: 'announcements', label: 'Duyurular' },
        { key: 'public-holidays', label: 'Resmi Tatiller' },
        { key: 'organization', label: 'Organizasyon Şeması' },
        ...(isAdmin
            ? [
                { key: 'states', label: 'İller' },
                { key: 'districts', label: 'İlçeler' },
                // { key: 'access-inventory', label: 'Erişim Envanteri' },
            ]
            : []),
        { key: 'system-settings', label: 'Sistem Ayarları' },
    ];

    const handleTabChange = (key: string) => {
        if (key === 'general') {
            // Genel sekmesine tıklandığında default olarak Roller'e yönlendir
            navigate('/settings/roles');
            return;
        }
        if (key === 'proposals') {
            // Teklif Yönetimi sekmesi için varsayılan alt sayfa
            navigate('/settings/statuses');
            return;
        }
        if (key.startsWith('grp-')) {
            const grp = modulesGroups.find(g => g.key === key);
            navigate(`/settings/${grp?.slugs[0] || 'professions'}`);
            return;
        }
    };


    return (
            <Tabs
                activeKey={activeKey}
                onChange={handleTabChange}
                className="custom-tabs mb-120px"
                tabBarStyle={{ borderBottom: 'none' }}
                destroyInactiveTabPane
            >
                <TabPane
                    tab={
                        <Button type={activeKey === 'general' ? 'primary' : 'default'} className='d-button tab-button'>Genel</Button>
                    }
                    key="general"
                >
                    <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ width: 220 }}>
                            <Menu
                                mode="inline"
                                selectedKeys={[tab && generalSlugs.includes(tab) ? tab : 'roles']}
                                items={generalMenuItems}
                                onClick={({ key }) => navigate(`/settings/${key}`)}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            {(tab === 'roles' || !tab || !generalSlugs.includes(tab)) && <RoleList />}
                            {tab === 'sms-tests' && <SmsTestList />}
                            {tab === 'agreements' && <AgreementList />}
                            {tab === 'announcements' && <AnnouncementList />}
                            {tab === 'system-settings' && <SettingKeyList />}
                            {tab === 'public-holidays' && <PublicHolidayList />}
                            {tab === 'organization' && <OrganizationChartSettings />}
                            {tab === 'states' && <StateList isActive={tab === 'states'} />}
                            {tab === 'districts' && <DistrictList isActive={tab === 'districts'} />}
                            {tab === 'access-inventory' && <AccessInventory />}
                        </div>
                    </div>
                </TabPane>
                <TabPane
                    tab={
                        <Button type={activeKey === 'proposals' ? 'primary' : 'default'} className='d-button tab-button'>Teklif Yönetimi</Button>
                    }
                    key="proposals"
                >
                    <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ width: 220 }}>
                            <Menu
                                mode="inline"
                                selectedKeys={[tab && proposalSlugs.includes(tab) ? tab : 'statuses']}
                                items={[
                                    { key: 'statuses', label: 'Statüler' },
                                    { key: 'discount-rates', label: 'İndirim Oranları' },
                                    { key: 'tools', label: 'Bileşenler' },
                                    { key: 'parameters', label: 'Parametre Yönetimi' },
                                    { key: 'control-areas', label: 'Parametre Tip-Özellik' },
                                    { key: 'offerings', label: 'Hizmet Grubu Yönetimi' },
                                    { key: 'groups', label: 'Kategori Yönetimi' },
                                    { key: 'negative-reasons', label: 'Olumsuz Teklif Nedenleri' },
                                    { key: 'nonconformity-reasons', label: 'Uygunsuzluk Nedenleri' }
                                ]}
                                onClick={({ key }) => navigate(`/settings/${key}`)}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            {(tab === 'statuses' || !tab || !proposalSlugs.includes(tab)) && <Statuses />}
                            {tab === 'discount-rates' && <DiscountRate />}
                            {tab === 'tools' && (
                                <ToolList
                                    questionToolId={questionToolIdValue}
                                    onQuestionDrawerChange={handleToolQuestionDrawerChange}
                                />
                            )}
                            {tab === 'parameters' && (
                                <ParameterList
                                    questionAttributeId={questionAttributeIdValue}
                                    onQuestionDrawerChange={handleParameterQuestionDrawerChange}
                                />
                            )}
                            {tab === 'control-areas' && <ControlAreaList />}
                            {tab === 'offerings' && <OfferingList />}
                            {tab === 'groups' && <GroupList />}
                            {tab === 'negative-reasons' && <NegativeProposalReasonList />}
                            {tab === 'nonconformity-reasons' && <NonconformityReasonList />}
                            {tab === 'system-settings' && <SettingKeyList />}
                        </div>
                    </div>
                </TabPane>
                {modulesGroups.map(group => (
                    <TabPane
                        tab={
                            <Button type={activeKey === group.key ? 'primary' : 'default'} className='d-button tab-button'>{group.label}</Button>
                        }
                        key={group.key}
                    >
                        {(() => {
                            const currentSlug = tab || group.slugs[0];
                            const isInGroup = group.slugs.includes(currentSlug);
                            const activeSlug = isInGroup ? currentSlug : group.slugs[0];
                            const items = group.slugs.map(s => ({
                                key: s,
                                label: (
                                    s === 'professions' ? 'Meslekler' :
                                    s === 'measuring-instruments' ? 'Ölçüm Cihazları' :
                                    s === 'measuring-instrument-inventories' ? 'Ölçüm Cihazı Envanteri' :
                                    s === 'measuring-instrument-calibrations' ? 'Cihaz Kalibrasyonları' :
                                    s === 'measuring-instrument-inspectors' ? 'Cihaz Denetçileri' :
                                    s === 'public-holidays' ? 'Resmi Tatiller' :
                                    s === 'inspectors' ? 'P.K.M Uzmanı' :
                                    s === 'inspector-professions' ? 'Denetçi Meslekleri' :
                                    s === 'leaves' ? 'İzinler' :
                                    s === 'external-documents' ? 'Dış Belgeler' :
                                    s === 'report-nodes' ? 'Rapor Yapısı' :
                                    s === 'inspection-appointments' ? 'Denetim Randevuları' :
                                    s === 'inspection-tasks' ? 'Denetim Görevleri' :
                                    s === 'questions' ? 'Sorular' :
                                    s === 'answers' ? 'Cevaplar' : s
                                )
                            }));
                            const isSingle = group.slugs.length === 1;
                            if (isSingle) {
                                // Tek alt sayfa: direkt içerik, sol menü olmadan
                                const only = group.slugs[0];
                                return (
                                    <div style={{ flex: 1 }}>
                                        {only === 'inspectors' && <InspectorList />}
                                        {only === 'professions' && <ProfessionList />}
                                        {only === 'external-documents' && <ExternalDocumentList />}
                                    </div>
                                );
                            }
                            return (
                                <div style={{ display: 'flex', gap: 16 }}>
                                    <div style={{ width: 260 }}>
                                        <Menu
                                            mode='inline'
                                            selectedKeys={[activeSlug]}
                                            items={items}
                                            onClick={({ key }) => navigate(`/settings/${key}`)}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        {activeSlug === 'professions' && <ProfessionList />}
                                        {activeSlug === 'measuring-instruments' && <MeasuringInstrumentList />}
                                        {activeSlug === 'public-holidays' && <PublicHolidayList />}
                                        {activeSlug === 'measuring-instrument-inventories' && <MeasuringInstrumentInventoryList />}
                                        {activeSlug === 'measuring-instrument-calibrations' && <MeasuringInstrumentCalibrationList />}
                                        {activeSlug === 'measuring-instrument-inspectors' && <MeasuringInstrumentInspectorList />}
                                        {activeSlug === 'inspectors' && <InspectorList />}
                                        {activeSlug === 'inspector-professions' && <InspectorProfessionList />}
                                        {activeSlug === 'leaves' && <LeaveList />}
                                        {activeSlug === 'external-documents' && <ExternalDocumentList />}
                                        {activeSlug === 'report-nodes' && <ReportNodeList />}
                                        {activeSlug === 'inspection-appointments' && <InspectionAppointmentList />}
                                        {activeSlug === 'inspection-tasks' && <InspectionTaskList />}
                                        {activeSlug === 'questions' && <QuestionList />}
                                        {activeSlug === 'answers' && <AnswerList />}
                                    </div>
                                </div>
                            );
                        })()}
                    </TabPane>
                ))}
                
                
            </Tabs>
    );
};

export default Settings;
